import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

// Services
import { NavigationParam, RouteParam, ServiceBase } from '../service-base';

// Modèles
import { BankAccountLine } from 'src/app/@shared/models/banking/bank-account-lines/bank-account-line.model';
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';
import { Loan } from 'src/app/@shared/models/banking/loans/loan.model';
import { LoanEvent } from 'src/app/@shared/models/banking/loans/loan-event.model';
import { LoanInstalment } from 'src/app/@shared/models/banking/loans/loan-instalment.model';

// Utils
import { ODataResponse } from 'src/app/@shared/utilities/odata/response';

const CLASS_NAME = 'Loan';
const ENDPOINT = `/loans/`;
const NAVIGATIONS: NavigationParam[] = [
    { key: 'schedule', path: 'schedule/', childClass: 'LoanInstalment' },
]
const ROUTES: RouteParam[] = [
    { key: 'massDelete', path: 'mass-delete/' },
    { key: 'updateInstalment', path: '{id}/instalments/{instalmentId}/' },
    { key: 'matchInstalment', path: '{id}/instalments/{instalmentId}/match/' },
    { key: 'markReimbursed', path: '{id}/mark-reimbursed/' },
    { key: 'events', path: '{id}/events/' },
    { key: 'cancelEvent', path: '{id}/events/{eventId}/' },
    { key: 'schedulePdf', path: '{id}/schedule/$value/' },
]

@Injectable({
    providedIn: 'root'
})
export class LoanService extends ServiceBase {
    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addNavigations(NAVIGATIONS);
        this.addRoutes(ROUTES);
    }

    /**
     * Annule le dernier événement de vie du prêt (soft delete) et régénère
     * l'échéancier. Retourne l'échéancier régénéré.
     */
    public cancelEvent$(loan: Loan, event: LoanEvent): Observable<LoanInstalment[]> {
        let url = this.routes['cancelEvent']
            .replace('{id}', loan.id.toString())
            .replace('{eventId}', event.id.toString());
        return this.httpClient.delete(url).pipe(
            map((body: any) => ((body as any[]) ?? []).map(
                item => GeneralFactory.get('LoanInstalment', item) as LoanInstalment
            ))
        );
    }

    /**
     * Enregistre un événement de vie du prêt (renégociation, remboursement anticipé,
     * suspension) et régénère les échéances à venir. Retourne l'échéancier régénéré.
     */
    public createEvent$(loan: Loan, event: LoanEvent): Observable<LoanInstalment[]> {
        let url = this.routes['events'].replace('{id}', loan.id.toString());
        return this.httpClient.post(url, this._toApi(event)).pipe(
            map((body: any) => ((body as any[]) ?? []).map(
                item => GeneralFactory.get('LoanInstalment', item) as LoanInstalment
            ))
        );
    }

    /**
     * Tableau d'amortissement mis en page (PDF) : récapitulatif, événements de vie et
     * échéancier avec les échéances remboursées surlignées. Renvoie le Blob PDF.
     */
    public downloadSchedulePdf$(loan: Loan): Observable<Blob> {
        let url = this.routes['schedulePdf'].replace('{id}', loan.id.toString());
        this._downloading$.next(true);
        return this.httpClient.get(url, {
            responseType: 'blob'
        }).pipe(
            finalize(() => this._downloading$.next(false)),
            map(response => response)
        );
    }

    /**
     * Reprise d'historique : marque remboursées (paid_historical) les échéances à
     * venir jusqu'à la date fournie (format AAAA-MM-JJ), sans lien bancaire.
     */
    public markReimbursed$(loan: Loan, until: string): Observable<void> {
        let url = this.routes['markReimbursed'].replace('{id}', loan.id.toString());
        return this.httpClient.put(url, { until: until }).pipe(
            map((body: any) => {
                let response = new ODataResponse(body);
                this.messageService.sendODataMessages(response.messages);
            })
        );
    }

    /**
     * Rapproche une échéance « à venir » d'une ligne bancaire (remboursement au
     * débit, montant exact). Pose le lien, bascule l'échéance en paid_matched et
     * régénère la ventilation comptable. Retourne l'échéancier régénéré.
     */
    public matchInstalment$(loan: Loan, instalment: LoanInstalment, bankAccountLine: BankAccountLine): Observable<LoanInstalment[]> {
        let url = this.routes['matchInstalment']
            .replace('{id}', loan.id.toString())
            .replace('{instalmentId}', instalment.id.toString());
        return this.httpClient.put(url, { bankAccountLine: bankAccountLine.id }).pipe(
            map((body: any) => ((body as any[]) ?? []).map(
                item => GeneralFactory.get('LoanInstalment', item) as LoanInstalment
            ))
        );
    }

    /**
     * Tableau d'amortissement complet de l'emprunt, trié par numéro d'échéance.
     */
    public schedule$(loan: Loan): Observable<LoanInstalment[]> {
        return this.getRelated$(loan, 'schedule') as Observable<LoanInstalment[]>;
    }

    /**
     * Dé-rapproche une échéance : retour à pending, coupure du lien et reprise de la
     * ventilation (imputation capital par défaut). Retourne l'échéancier régénéré.
     */
    public unmatchInstalment$(loan: Loan, instalment: LoanInstalment): Observable<LoanInstalment[]> {
        let url = this.routes['matchInstalment']
            .replace('{id}', loan.id.toString())
            .replace('{instalmentId}', instalment.id.toString());
        return this.httpClient.delete(url).pipe(
            map((body: any) => ((body as any[]) ?? []).map(
                item => GeneralFactory.get('LoanInstalment', item) as LoanInstalment
            ))
        );
    }

    /**
     * Édition d'une échéance (parts capital/intérêts/assurance, date) ou bascule
     * pending <-> paid_historical. Retourne l'échéancier ré-enchaîné.
     */
    public updateInstalment$(loan: Loan, instalment: LoanInstalment): Observable<LoanInstalment[]> {
        let url = this.routes['updateInstalment']
            .replace('{id}', loan.id.toString())
            .replace('{instalmentId}', instalment.id.toString());
        return this.httpClient.put(url, this._toApi(instalment)).pipe(
            map((body: any) => ((body as any[]) ?? []).map(
                item => GeneralFactory.get('LoanInstalment', item) as LoanInstalment
            ))
        );
    }
}
